import { Link } from '@tanstack/react-router'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { Wand2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, type KeyboardEvent } from 'react'

import { annotationKindOf, KIND_LABEL, type AssetCard as AssetCardData } from '@/api/assets'
import { Button } from '@/components/ui/button'
import { cn } from '@/components/ui/utils'
import { formatRelative } from '@/lib/format'
import { ANNOTATE_ROUTE_ID } from '@/routes/annotate.$kind.$uid'
import { useLibraryStore } from '@/stores/libraryStore'

import { selectionMode } from './AssetCard'
import { StatusBadge } from './StatusBadge'

/**
 * Dense list view: 40 px rows, the same selection model as the grid.
 *
 * This is the view for "find the four plates whose horizon is wrong" — it
 * trades the picture for the status, the category and the timestamp on one
 * line. The thumb is still there, 28 px wide, because recognising art by
 * name alone is slower than it looks.
 */

export interface AssetListProps {
  items: AssetCardData[]
  onLoadMore?(): void
  hasMore?: boolean
  loadingMore?: boolean
  className?: string
  'aria-label'?: string
}

const ROW_HEIGHT = 40

export function AssetList({ items, onLoadMore, hasMore = false, loadingMore = false, className, 'aria-label': ariaLabel = 'Assets' }: AssetListProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const selected = useLibraryStore((s) => s.selected)
  const rovingIndex = useLibraryStore((s) => s.rovingIndex)
  const select = useLibraryStore((s) => s.select)
  const selectMany = useLibraryStore((s) => s.selectMany)
  const setRoving = useLibraryStore((s) => s.setRoving)
  const setOrder = useLibraryStore((s) => s.setOrder)

  const uids = useMemo(() => items.map((a) => a.uid), [items])
  useEffect(() => {
    setOrder(uids)
  }, [uids, setOrder])

  const virtualiser = useWindowVirtualizer({
    count: items.length,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    scrollMargin: containerRef.current?.offsetTop ?? 0,
  })

  const rows = virtualiser.getVirtualItems()
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : undefined
  useEffect(() => {
    if (!hasMore || loadingMore || !onLoadMore) return
    if (lastRow && lastRow.index >= items.length - 10) onLoadMore()
  }, [hasMore, items.length, lastRow, loadingMore, onLoadMore])

  const moveTo = useCallback(
    (next: number, extend: boolean) => {
      const clamped = Math.max(0, Math.min(items.length - 1, next))
      const uid = items[clamped]?.uid
      if (!uid) return
      setRoving(clamped)
      select(uid, extend ? 'range' : 'replace')
      virtualiser.scrollToIndex(clamped, { align: 'auto' })
      requestAnimationFrame(() => containerRef.current?.querySelector<HTMLElement>(`[data-uid="${CSS.escape(uid)}"]`)?.focus())
    },
    [items, select, setRoving, virtualiser],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          moveTo(rovingIndex + 1, e.shiftKey)
          break
        case 'ArrowUp':
          e.preventDefault()
          moveTo(rovingIndex - 1, e.shiftKey)
          break
        case 'Home':
          e.preventDefault()
          moveTo(0, e.shiftKey)
          break
        case 'End':
          e.preventDefault()
          moveTo(items.length - 1, e.shiftKey)
          break
        case 'a':
        case 'A':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault()
            selectMany(uids)
          }
          break
        default:
          break
      }
    },
    [items.length, moveTo, rovingIndex, selectMany, uids],
  )

  if (items.length === 0) return null
  const cursor = Math.min(rovingIndex, items.length - 1)

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-multiselectable
      aria-label={ariaLabel}
      data-testid="asset-list"
      onKeyDown={onKeyDown}
      className={cn('card w-full overflow-hidden', className)}
    >
      <div style={{ height: virtualiser.getTotalSize(), position: 'relative' }}>
        {rows.map((row) => {
          const asset = items[row.index]
          if (!asset) return null
          // Only stages have a hand editor now; characters and objects are puppets.
          const annotateKind = annotationKindOf(asset.kind) === 'stage' ? 'stage' : null
          const isSelected = selected.has(asset.uid)
          return (
            <div
              key={asset.uid}
              role="option"
              aria-selected={isSelected}
              aria-label={asset.name}
              data-uid={asset.uid}
              data-testid="asset-row"
              tabIndex={row.index === cursor ? 0 : -1}
              onFocus={() => (row.index === cursor ? undefined : setRoving(row.index))}
              onClick={(e) => select(asset.uid, selectionMode(e))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  select(asset.uid, e.key === ' ' ? 'toggle' : selectionMode(e))
                }
              }}
              className={cn(
                'absolute left-0 top-0 flex w-full cursor-pointer items-center gap-3 px-3 text-sm',
                'border-b border-line last:border-b-0 focus-visible:focus-ring hover:bg-paper-2',
                isSelected && 'bg-accent-soft',
              )}
              style={{ height: ROW_HEIGHT, transform: `translateY(${row.start - (virtualiser.options.scrollMargin ?? 0)}px)` }}
            >
              {asset.media?.thumb && asset.kind !== 'audio' ? (
                <img src={asset.media.thumb} alt="" loading="lazy" decoding="async" className="size-7 shrink-0 rounded bg-checker object-contain" />
              ) : (
                <span className="size-7 shrink-0 rounded bg-paper-2" aria-hidden />
              )}
              <span className="min-w-0 flex-1 truncate font-medium text-ink" title={asset.uid}>
                {asset.name}
              </span>
              <span className="hidden w-28 shrink-0 truncate text-xs text-ink-3 md:block">{asset.category ?? asset.world ?? KIND_LABEL[asset.kind]}</span>
              <StatusBadge review={asset.review} assetStatus={asset.status} showConfidence={false} className="shrink-0" />
              <span className="hidden w-24 shrink-0 text-right text-xs text-ink-3 lg:block">{formatRelative(asset.updated_at)}</span>
              {annotateKind ? (
                <Button asChild size="sm" variant="ghost" className="shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Link to={ANNOTATE_ROUTE_ID} params={{ kind: annotateKind, _splat: asset.uid }} aria-label={`Annotate ${asset.name}`}>
                    <Wand2 aria-hidden />
                  </Link>
                </Button>
              ) : (
                <span className="size-9 shrink-0" aria-hidden />
              )}
            </div>
          )
        })}
      </div>

      {hasMore ? (
        <div className="flex justify-center border-t border-line py-3">
          <Button variant="ghost" size="sm" onClick={() => onLoadMore?.()} loading={loadingMore}>
            Show more
          </Button>
        </div>
      ) : null}
    </div>
  )
}
